import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CREATE } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

const EMOTIONS = ["melancholy", "rage", "serenity", "anxiety", "euphoria"];

export default function CreatePost() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState("");
  const [emotion, setEmotion] = useState("");
  const [mediaFile, setMediaFile] = useState(null);
  const [visualizeModel, setVisualizeModel] = useState("gemini");
  const [generatedPath, setGeneratedPath] = useState(null);
  const [visualizing, setVisualizing] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0b0c10]">
        <Navbar />
        <div className="max-w-2xl mx-auto p-10 text-slate-300 text-center">
          Please <a href="/login" className="underline text-yellow-400">sign in</a> to post.
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let media_path = null;
      if (generatedPath) {
        media_path = generatedPath;
      } else if (mediaFile) {
        const fd = new FormData();
        fd.append("file", mediaFile);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        media_path = data.storage_path;
      }
      await api.post("/posts", {
        content,
        emotion: emotion || null,
        media_path,
      });
      toast.success("Mentation published");
      navigate("/feed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Publish failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0c10] telesis-grain">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 md:px-6 py-10">
        <p className="telesis-eyebrow">New mentation</p>
        <h1 className="text-2xl md:text-3xl font-bold mt-1 mb-6 text-slate-100">Post to the ledger</h1>
        <form data-testid={CREATE.form} onSubmit={submit} className="telesis-panel rounded p-6 space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Content</Label>
            <Textarea
              data-testid={CREATE.content}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
              maxLength={2000}
              rows={6}
              placeholder="What should enter mentation?"
              className="mt-1 bg-[#12141c] border-[#222634] text-slate-100 resize-none"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Emotion (optional · AI will tag if left blank)</Label>
            <Select value={emotion} onValueChange={setEmotion}>
              <SelectTrigger data-testid={CREATE.emotionSelect} className="mt-1 bg-[#12141c] border-[#222634] text-slate-100">
                <SelectValue placeholder="Let AI decide" />
              </SelectTrigger>
              <SelectContent className="bg-[#12141c] border-[#222634] text-slate-100">
                {EMOTIONS.map((e) => (
                  <SelectItem key={e} value={e}>{e}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Image or video (optional · images ≤10MB · videos ≤50MB)</Label>
            <input
              data-testid={CREATE.mediaInput}
              type="file"
              accept="image/*,video/mp4,video/webm,video/quicktime"
              onChange={(e) => {
                setMediaFile(e.target.files?.[0] || null);
                setGeneratedPath(null);
              }}
              className="mt-1 w-full text-sm text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-yellow-400 file:text-black file:text-xs file:font-semibold hover:file:bg-yellow-300"
            />
            {mediaFile && (
              <p className="mt-1 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                {mediaFile.name} · {(mediaFile.size / (1024 * 1024)).toFixed(2)} MB
              </p>
            )}
          </div>

          <div className="rounded border border-dashed border-[#2a2f3f] p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="telesis-eyebrow">Or visualize this mentation</p>
              <Select value={visualizeModel} onValueChange={setVisualizeModel}>
                <SelectTrigger
                  data-testid={CREATE.visualizeModel}
                  className="w-[180px] bg-[#12141c] border-[#222634] text-slate-100 h-8 text-xs"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#12141c] border-[#222634] text-slate-100">
                  <SelectItem value="gemini">Nano Banana</SelectItem>
                  <SelectItem value="openai">GPT Image 1</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid={CREATE.visualizeBtn}
              type="button"
              variant="outline"
              disabled={visualizing || content.trim().length < 3}
              onClick={async () => {
                setVisualizing(true);
                try {
                  const { data } = await api.post(
                    "/image/generate",
                    { prompt: content, model: visualizeModel },
                    { timeout: 120000 }
                  );
                  setGeneratedPath(data.storage_path);
                  setMediaFile(null);
                  toast.success("Visualized · will attach on publish");
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Generation failed");
                } finally {
                  setVisualizing(false);
                }
              }}
              className="w-full rounded-full bg-transparent border-[#2a2f3f] text-slate-100 hover:bg-[#1a1d28]"
            >
              {visualizing ? "Summoning imagery…" : "✧ Visualize this mentation"}
            </Button>
            {generatedPath && (
              <div className="relative">
                <img
                  data-testid={CREATE.visualizePreview}
                  src={`${API}/files/${generatedPath}`}
                  alt="generated preview"
                  className="w-full max-h-64 object-cover rounded"
                />
                <button
                  data-testid={CREATE.visualizeClear}
                  type="button"
                  onClick={() => setGeneratedPath(null)}
                  className="absolute top-2 right-2 bg-black/70 text-slate-200 text-xs px-2 py-1 rounded hover:bg-black/90"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          <Button
            data-testid={CREATE.publish}
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-yellow-400 text-black hover:bg-yellow-300 font-semibold"
          >
            {busy ? "Publishing…" : "Publish mentation"}
          </Button>
        </form>
      </main>
    </div>
  );
}
